import type { ExecutorResponse } from '@/core/generated/api';
import { executorService } from '../api/executorService';
import { executorSearchService } from '../api/executorSearchService';
import type { AsyncSearchOption } from '@/shared/ui/AsyncSearchField';
import { PersonSearchField } from '@/shared/ui/PersonSearchField/PersonSearchField';
import { PersonOption } from '@/shared/ui/PersonOption/PersonOption';
import type { ReactNode } from 'react';

export type ExecutorSearchOption = AsyncSearchOption<string, ExecutorResponse>;

interface ExecutorSearchFieldProps {
    name: string;
    label: string;
    value: ExecutorResponse | null;

    /** Идентификатор выбранного исполнителя при восстановлении фильтра */
    valueId?: string | null;
    onChange: (executor: ExecutorResponse | null) => void;
    itemTemplate?: (option: ExecutorSearchOption) => ReactNode;
    error?: string;
    disabled?: boolean;
    isRequired?: boolean;
    placeholder?: string;
    minSearchLength?: number;
    debounceMs?: number;
    panelClassName?: string;
    scrollHeight?: string;
    takeCount?: number;
}

/** Формирует строковое представление исполнителя для выбранного значения */
const getExecutorOptionName = (executor: ExecutorResponse): string => (
    [[executor.name, executor.lastName].filter(Boolean).join(' '), executor.phone].filter(Boolean).join(' · ') || 'Исполнитель без имени'
);

/** Преобразует найденного исполнителя в вариант поиска */
const toExecutorOption = (executor: ExecutorResponse): ExecutorSearchOption | null => (
    executor.id
        ? {
            id: executor.id,
            name: getExecutorOptionName(executor),
            data: executor
        }
        : null
);

/** Загружает варианты исполнителей */
const searchExecutorOptions = async (
    query: string,
    takeCount: number
): Promise<ExecutorSearchOption[]> => (
    (await executorSearchService.search(query, takeCount))
        .map(toExecutorOption)
        .filter((option): option is ExecutorSearchOption => option !== null)
        .slice(0, takeCount)
);

/** Загружает исполнителя для выбранного значения */
const resolveExecutorOption = async (id: string): Promise<ExecutorSearchOption | null> =>
    toExecutorOption(await executorService.getById(id));

/** Предоставляет поиск исполнителя */
export const ExecutorSearchField = (
    {
        name,
        label,
        value,
        valueId,
        onChange,
        itemTemplate,
        error,
        disabled = false,
        isRequired = false,
        placeholder = 'Введите ФИО исполнителя',
        minSearchLength = 2,
        debounceMs = 400,
        panelClassName,
        scrollHeight,
        takeCount = 12,
    }: ExecutorSearchFieldProps
) => {

    const selectedOption = value
        ? toExecutorOption(value)
        : null;

    const selectedId = selectedOption?.id ?? valueId ?? null;

    return (
        <PersonSearchField<ExecutorResponse>
            name={name}
            label={label}
            value={selectedId}
            resolveOption={resolveExecutorOption}
            onChange={(_, option) => onChange(option?.data ?? null)}
            loadOptions={(query) => searchExecutorOptions(query, takeCount)}
            initialOption={selectedOption}
            itemTemplate={itemTemplate ?? ((option) => <PersonOption
                name={[option.data?.name, option.data?.lastName].filter(Boolean).join(' ') || 'Исполнитель без имени'}
                phone={option.data?.phone}
                photoUrl={option.data?.photoUrl}
                details={option.data?.cityName ?? undefined}
                blocked={option.data?.isBlocked}
            />)}
            selectedItemTemplate={(option) => option.name}
            panelClassName={panelClassName}
            scrollHeight={scrollHeight}
            error={error}
            disabled={disabled}
            isRequired={isRequired}
            placeholder={placeholder}
            minSearchLength={minSearchLength}
            debounceMs={debounceMs}
            emptyMessage="Исполнители не найдены"
            searchErrorMessage="Не удалось найти исполнителя"
        />
    );
};
